import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { MaggieHttp } from '../../services/MaggieHttp';
import { MaggieFeatureFlags } from '../../util/feature-flags';

async function fetchSiteFeatureKeys(): Promise<string[]> {
  // A 404 means this site's api predates the route; the gate already fails open, so it is not an error.
  const { data } = await MaggieHttp.maggie.get<{ features: string[] }>('/auth/entitlements', {
    skipSentryStatuses: [404],
  });
  return data.features;
}

/**
 * Frontegg feature keys the site's own plans include. The api answers for the tenant whose site this is,
 * so staff viewing a client see that client's plan rather than their own login's. Read only while a flag
 * that uses it is on; `keys` stays undefined while loading and when the read fails.
 */
export function useSiteFeatureKeys(): { keys: string[] | undefined; isLoading: boolean } {
  const { showFrostedModules, useFeatureEntitlements } = useFlags<MaggieFeatureFlags>();
  const { data, isLoading } = useQuery({
    queryKey: ['site-plan-features'],
    queryFn: fetchSiteFeatureKeys,
    enabled: Boolean(showFrostedModules || useFeatureEntitlements),
    // The plan rarely changes mid-session, and a failed read must not retry on every gate that mounts.
    staleTime: Infinity,
    // One retry so a blip does not leave the whole session unfrosted; a 404 is an api without the route.
    retry: (failureCount, error) =>
      failureCount < 1 && !(isAxiosError(error) && error.response?.status === 404),
    retryOnMount: false,
  });
  return { keys: data, isLoading };
}
